import { FirebaseApp, initializeApp } from 'firebase/app';
import { Auth, getAuth } from 'firebase/auth';
import { ReactNode, createContext, useContext } from 'react';

const envFirebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const prodFirebaseConfig = {
    apiKey: "AIzaSyDm3iCXmNq6s40WkEYfjSrx-qTfFfkh9C0",
    authDomain: "baila-740b8.firebaseapp.com",
    projectId: "baila-740b8",
    storageBucket: "baila-740b8.appspot.com",
    messagingSenderId: "318048668868",
    appId: "1:318048668868:web:dad7afabb11295feb50d91",
    measurementId: "G-KKW5N6W8SV",
};

const devFirebaseConfig = {
    apiKey: "AIzaSyDK-8yoKa2tdqJ7tmUeFqW56IS4OpkbcRc",
    authDomain: "baila-dev.firebaseapp.com",
    projectId: "baila-dev",
    storageBucket: "baila-dev.firebasestorage.app",
    messagingSenderId: "681617095309",
};

function hasFirebaseEnvOverride(): boolean {
    return Object.values(envFirebaseConfig).some((value) => Boolean(value));
}

function getDefaultFirebaseConfig() {
    if (process.env.NODE_ENV === 'development') {
        return devFirebaseConfig;
    }
    return prodFirebaseConfig;
}

// Default local development to baila-dev so localhost checkout talks to the
// same Firebase project as the local Flask API unless explicitly overridden.
const firebaseConfig = {
    ...getDefaultFirebaseConfig(),
    ...Object.fromEntries(
        Object.entries(envFirebaseConfig).filter(([, value]) => Boolean(value)),
    ),
};

if (process.env.NODE_ENV === 'development' && !hasFirebaseEnvOverride()) {
    // eslint-disable-next-line no-console
    console.info('Using default Firebase development config for baila-dev.');
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Define the shape of your Firebase context data
type FirebaseContextData = {
    app: FirebaseApp;
    auth: Auth;
};

// Create a context instance
const FirebaseContext = createContext<FirebaseContextData | undefined>(undefined);

// Define a custom hook for using the Firebase context
export function useFirebase() {
    const context = useContext(FirebaseContext);
    if (!context) {
        throw new Error('useFirebase must be used within a FirebaseProvider');
    }
    return context;
}

// Define the context provider component
type FirebaseProviderProps = {
    children: ReactNode;
};

export function FirebaseProvider({ children }: FirebaseProviderProps) {
    const contextValue: FirebaseContextData = {
        app,
        auth,
    };

    return <FirebaseContext.Provider value={contextValue}>{children}</FirebaseContext.Provider>;
}
